import React from "react";
import { PageHero, Section, SectionTitle, Reveal, CTALink, Eyebrow, GoldRule } from "@/components/Editorial";

const INTRO =
  "Established in 2001, Hema Connoisseur Collections is an Indian import and distribution house representing distinguished alcoholic beverage producers across hospitality, retail, diplomatic and institutional markets. The company has grown through long-standing trade relationships, structured distribution, operational discipline and a practical understanding of India's beverage landscape.";

const PVM = [
  {
    title: "Purpose",
    body: "To give distinguished global beverage houses a disciplined, credible route into India's most demanding trade channels.",
  },
  {
    title: "Vision",
    body: "To be India's trusted partner for premium beverage market access — measured by relationships and consistency, not volume alone.",
  },
  {
    title: "Mission",
    body: "To combine curation, compliance and infrastructure so that every brand we represent is positioned with care and supplied with dependability.",
  },
];

const TIMELINE = [
  { marker: "2001", title: "Foundation", body: "Hema Connoisseur Collections is established in Delhi, importing and distributing premium alcoholic beverages for India's trade." },
  { marker: "Infrastructure", title: "Bonded warehousing", body: "A private custom-bonded warehouse of approximately 10,000 sq. ft. is established, followed by additional warehousing capacity of over 15,000 sq. ft." },
  { marker: "Portfolio", title: "Portfolio growth", body: "The portfolio expands across wine, beer and spirits, drawing from respected producers in Europe and the Americas." },
  { marker: "Today", title: "A national trade network", body: "HCC serves hospitality, retail, diplomatic and institutional markets — combining direct Delhi NCR logistics with partner distribution across India." },
];

const PHILOSOPHY = [
  { title: "Curation before scale", body: "Every producer we represent is selected for quality, character and market relevance — the portfolio is curated, not accumulated." },
  { title: "Compliance as foundation", body: "Import documentation, bonded storage and regulatory discipline underpin every movement of stock." },
  { title: "Relationships over transactions", body: "Two decades of trade relationships across channels give our brands durable market presence." },
  { title: "Consistency in supply", body: "Structured logistics and inventory organisation keep availability dependable for every client we serve." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-[#F7F1E7]">
      <PageHero eyebrow="About HCC" title="Built on taste, trade and trust." lede={INTRO} />

      <Section tint>
        <div className="grid md:grid-cols-3 gap-10 max-w-6xl">
          {PVM.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12}>
              <Eyebrow>{p.title}</Eyebrow>
              <p className="mt-4 hcc-serif text-xl leading-relaxed text-[#1c1713]">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section dark>
        <SectionTitle dark eyebrow="Our Journey" title="From a Delhi founding to a national trade network." />
        <div className="max-w-3xl space-y-0">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.marker} delay={i * 0.08}>
              <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[160px_1fr] gap-6 border-l border-[#C7A45D]/40 pl-6 pb-12 relative">
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#C7A45D]" aria-hidden="true" />
                <span className="hcc-serif text-xl sm:text-2xl text-[#C7A45D]">{t.marker}</span>
                <div>
                  <h3 className="text-sm uppercase tracking-[0.18em] text-[#F7F1E7]/90">{t.title}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-[#F7F1E7]/60 max-w-xl">{t.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionTitle eyebrow="Operating Philosophy" title="How we work." />
        <div className="grid sm:grid-cols-2 gap-x-14 gap-y-10 max-w-5xl">
          {PHILOSOPHY.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <h3 className="hcc-serif text-2xl text-[#1c1713]">{p.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/70">{p.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tint>
        <div className="grid md:grid-cols-2 gap-14 max-w-6xl">
          <Reveal>
            <Eyebrow>Trade Relationships</Eyebrow>
            <h3 className="hcc-serif text-2xl mt-4 text-[#1c1713]">An institutional network built since 2001.</h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-[#1c1713]/70">
              HCC maintains more than ten thousand trade and institutional client touchpoints across
              hospitality, retail, diplomatic and institutional channels — relationships developed
              through consistent supply and restrained, professional representation.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <Eyebrow>Compliance & Responsibility</Eyebrow>
            <h3 className="hcc-serif text-2xl mt-4 text-[#1c1713]">Regulatory understanding as a capability.</h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-[#1c1713]/70">
              India's beverage regulations differ by state and channel. HCC treats regulatory
              understanding, documentation and responsible communication as core disciplines of the
              business, not administrative afterthoughts.
            </p>
          </Reveal>
        </div>
      </Section>

      <Section>
        <div className="grid md:grid-cols-2 gap-14 max-w-6xl">
          <Reveal>
            <Eyebrow>Leadership</Eyebrow>
            <div className="mt-6 border border-[#1c1713]/15 px-8 py-10" data-testid="leadership-placeholder">
              <p className="hcc-serif text-xl text-[#1c1713]">Leadership profiles</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/60">
                Profiles of the leadership team are being prepared for publication and will appear here.
              </p>
              <GoldRule className="mt-6" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <Eyebrow>Warehousing</Eyebrow>
            <h3 className="hcc-serif text-2xl mt-4 text-[#1c1713]">Customs-bonded storage under direct control.</h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-[#1c1713]/70">
              A private custom-bonded warehouse of approximately 10,000 sq. ft., supported by over
              15,000 sq. ft. of additional capacity, a company-owned fleet and a permanent logistics
              team in Delhi NCR.
            </p>
            <div className="mt-6">
              <CTALink to="/infrastructure" testId="about-infrastructure-cta">View Infrastructure</CTALink>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section dark className="text-center">
        <Reveal>
          <GoldRule className="mx-auto" />
          <h2 className="hcc-serif text-3xl sm:text-4xl font-medium mt-8 max-w-xl mx-auto">Represent your brand in India with HCC.</h2>
          <div className="mt-9 flex justify-center gap-4 flex-wrap">
            <CTALink to="/partner" dark solid testId="about-partner-cta">Partner With HCC</CTALink>
            <CTALink to="/contact" dark testId="about-contact-cta">Contact Our Team</CTALink>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
